import type { ReactElement } from 'react';
import * as React from 'react';

import classNames from 'classnames';
import moment from 'moment';
import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { SHORT_MONTH_DAY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';

import _t from 'i18n!nls/phoenix-cdp';

type InputProps = {
  course: Pick<
    FormattedCourse,
    'plannedLaunchDate' | 'upcomingSessionStartDate' | 'isLaunched' | 'isPreEnroll' | 'isSessionsEnabled'
  >;
  isS12nLaunched?: boolean;
  className?: string;
  customStyle?: React.CSSProperties;
};

type Props = InputProps & {
  sessionsV2EnrollmentEnabled: boolean;
  router?: InjectedRouter;
};

export enum CourseModes {
  Session = 'SESSION',
  OnDemand = 'ONDEMAND',
}

const getStartDateMessageForClearerStartDateOnXdpEnrollCTATest = (startDate: string): string => {
  const clearerStartDateOnXdpEnrollCTAVariant = GoogleCertificateEpicClient.preview('clearerStartDateOnXdpEnrollCTA');
  switch (clearerStartDateOnXdpEnrollCTAVariant) {
    case '1':
      return _t('Starts today');
    case '2':
      return '';
    default: // control case
      return _t('Starts {startDate}', { startDate });
  }
};

export const getFormattedStartDate = (
  course: Pick<
    FormattedCourse,
    'plannedLaunchDate' | 'upcomingSessionStartDate' | 'isLaunched' | 'isPreEnroll' | 'isSessionsEnabled'
  >,
  sessionsV2EnrollmentEnabled: boolean,
  privateSessionStart?: number,
  isS12nLaunched?: boolean
): ReactElement | null => {
  if (!course) {
    return null;
  }
  const { plannedLaunchDate, upcomingSessionStartDate, isLaunched, isPreEnroll, isSessionsEnabled } = course;
  let startDate: string | undefined;
  let startDateMessage: string;

  if (privateSessionStart) {
    startDate = formatDateTimeDisplay(privateSessionStart, 'MMM D');
    startDateMessage = _t('Starts {startDate}');
  } else if (isPreEnroll) {
    // Parse in english and convert it to the relevant locale
    startDate = formatDateTimeDisplay(
      moment(plannedLaunchDate, ['MM-DD-YYYY', 'MMM DD YYYY', 'DD MMM YYYY', 'MMM YYYY', 'MMM Do YYYY'], 'en').locale(
        _t.getLocale()
      ),
      SHORT_MONTH_DAY_DISPLAY
    );

    startDateMessage = _t('Starts {startDate}');
  } else if (isS12nLaunched) {
    const now = moment().locale(_t.getLocale());
    startDate = formatDateTimeDisplay(now, SHORT_MONTH_DAY_DISPLAY);
    startDateMessage = getStartDateMessageForClearerStartDateOnXdpEnrollCTATest(startDate);
  } else if (isLaunched) {
    if (isSessionsEnabled && upcomingSessionStartDate) {
      const upcoming = moment(upcomingSessionStartDate).locale(_t.getLocale());
      const now = moment().locale(_t.getLocale());
      if (sessionsV2EnrollmentEnabled) {
        startDate = formatDateTimeDisplay(now, SHORT_MONTH_DAY_DISPLAY);
        startDateMessage = getStartDateMessageForClearerStartDateOnXdpEnrollCTATest(startDate);
      } else {
        startDate = formatDateTimeDisplay(upcoming, SHORT_MONTH_DAY_DISPLAY);
        startDateMessage = now.isAfter(upcoming, 'minutes') ? _t('Started {startDate}') : _t('Starts {startDate}');
      }
    } else if (isSessionsEnabled) {
      startDateMessage = _t('No Upcoming Session Available');
    } else {
      startDateMessage = _t('Self Paced');
    }
  } else {
    startDateMessage = _t('Coming Soon');
  }
  return <FormattedMessage message={startDateMessage} startDate={startDate} />;
};

class StartDateString extends React.Component<Props> {
  // TODO (rkhandelwal) FIXME session start date should not be ongoing session
  // TODO ensure other courseStatuses besides unlaunched can use this copy
  render() {
    const { className, course, sessionsV2EnrollmentEnabled, customStyle, isS12nLaunched } = this.props;
    const { upcomingSessionStartDate, isLaunched, isPreEnroll, isSessionsEnabled } = course || {};

    // Check if the course  or s12n is eligible for the clearer start date on xdp enroll cta test
    // The s12n should be launched
    // The course should be launched and have a session start date and should be alwaysAvailable(sessionsV2EnrollmentEnabled=true)
    const isEligibleForClearerStartDateOnXdpEnrollCTATest =
      !isPreEnroll &&
      (isS12nLaunched || (isLaunched && isSessionsEnabled && upcomingSessionStartDate && sessionsV2EnrollmentEnabled));
    if (isEligibleForClearerStartDateOnXdpEnrollCTATest) {
      GoogleCertificateEpicClient.get('clearerStartDateOnXdpEnrollCTA');
    }

    if (
      isEligibleForClearerStartDateOnXdpEnrollCTATest &&
      GoogleCertificateEpicClient.preview('clearerStartDateOnXdpEnrollCTA') === '2'
    ) {
      return null;
    }

    const combinedClassName = classNames('rc-StartDateString font-xs', className);
    return (
      <div className={classNames('startdate', combinedClassName)} style={customStyle} data-testid="start-date-string">
        {getFormattedStartDate(course, sessionsV2EnrollmentEnabled, undefined, isS12nLaunched)}
      </div>
    );
  }
}

export default compose<Props, InputProps>(
  connectToRouter((router) => ({ router })),
  withSessionsV2EnrollmentEnabled(
    ({ course }) => course?.id,
    () => (
      <div className="startdate rc-StartDateString font-xs start-date-string" style={{ textIndent: '-9999px' }}>
        <span>{_t('Retrieving date')}</span>
      </div>
    )
  )
)(StartDateString);

export const BaseComponent = StartDateString;
